import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function BillingReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment: null, attempts: 0 });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=/billing/return?session_id=${sessionId}`);
      return;
    }
    if (!sessionId) {
      setState({ status: "error", payment: null });
      return;
    }

    let cancelled = false;
    const maxAttempts = 8;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment: data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment: data });
          return;
        }
        if (n + 1 >= maxAttempts) {
          setState({ status: "pending", payment: data });
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setState({ status: "error", payment: null });
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId, user, authLoading, navigate]);

  return (
    <div data-testid="billing-return-page" className="mx-auto max-w-xl px-6 py-20">
      <Card className="rounded-2xl border border-border/60 bg-card p-10 text-center shadow-sm">
        {state.status === "polling" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-5 font-serif text-3xl text-forest">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground">Just a moment — checking Stripe.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald" />
            <h1 className="mt-5 font-serif text-3xl text-forest">You're a Providence Partners member.</h1>
            <p className="mt-2 text-muted-foreground">
              Your $299 annual membership is active. Your facility is now listed publicly for the next 365 days.
            </p>
            <Button asChild className="mt-6 rounded-full bg-primary px-6 py-6 text-primary-foreground" data-testid="billing-paid-cta">
              <Link to="/dashboard/facility">Go to your dashboard <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-5 font-serif text-3xl text-forest">Still processing…</h1>
            <p className="mt-2 text-muted-foreground">
              Stripe is still confirming your payment. We'll activate your membership as soon as it clears.
              You can safely close this page — we'll email you when it's done.
            </p>
            <Button asChild variant="outline" className="mt-6 rounded-full px-6">
              <Link to="/dashboard/facility">Back to dashboard</Link>
            </Button>
          </>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-5 font-serif text-3xl text-forest">Payment didn't go through</h1>
            <p className="mt-2 text-muted-foreground">
              The checkout session expired or was canceled. No charge was made — you can try again from your dashboard.
            </p>
            <Button asChild className="mt-6 rounded-full bg-primary px-6 py-6 text-primary-foreground">
              <Link to="/dashboard/facility">Back to dashboard</Link>
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
